import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useForm } from "react-hook-form";
import { useSnackbar } from "@kitbird/core";
import { joiResolver } from "@hookform/resolvers/joi";
import {
  deleteMasterDepartment,
  getMasterDepartment,
  getMasterDepartmentCost,
  IGetMasterDepartmentData,
  postMasterDepartment,
  putMasterDepartment,
} from "../../../services/masterDepartmentService";
import useAuth from "../../../components/Authentication/useAuth";
import schema from "./schema";

export interface FilterI {
  company?: {
    label: string,
    value: string,
  },
  companyAccount?: {
    label: string,
    value: string,
  },
  department?: {
    label: string,
    value: string,
  },
  pool?: {
    label: string,
    value: string,
  },
  search: string,
  sortBy: string,
  sortType: "ASC" | "DSC" | "DEFAULT",
}

const useDepartment = () => {
  const snackbar = useSnackbar();
  const { userInfo } = useAuth();

  const [isLoading, setLoading] = useState(false);
  const [modalFormDepartment, setModalFormDepartment] = useState(false);
  const [modalDeleteDepartment, setModalDeleteDepartment] = useState(false);
  const [editValues, setEditValues] = useState<IGetMasterDepartmentData | undefined>();
  const [params, setParams] = useState<FilterI>({
    search: "",
    sortBy: "id",
    sortType: "DEFAULT",
    pool: { label: "Semua pool", value: "" },
    department: { label: "Semua departemen", value: "" },
  });

  const initDepartmentValues = {
    code_type: "",
    department: "",
    department_desc: "",
    department_cost: "",
  };

  const methods = useForm({
    defaultValues: initDepartmentValues,
    resolver: joiResolver(schema),
  });
  const {
    getValues,
    reset,
  } = methods;

  useEffect(() => {
    if (editValues) {
      reset({
        code_type: editValues.code_type,
        department: editValues.department,
        department_cost: editValues.department_cost,
        department_desc: editValues.department_desc,
      });
    } else reset(initDepartmentValues);
  }, [editValues]);

  const {
    data: dataMasterDepartment,
    isFetching,
    refetch: refetchTable,
  } = useQuery({
    queryKey: "get_master_department",
    queryFn: () => getMasterDepartment(),
  });

  const masterDepartmentList = useMemo(
    () => {
      let list = dataMasterDepartment?.lstmstdepartment || [];
      const sortBy = params.sortType === "DEFAULT" ? "id" : "department";
      const multiplier = params.sortType !== "DSC" ? 1 : -1;

      list.sort((a, b) => {
        const val = (a[sortBy] > b[sortBy] ? 1 : -1) * multiplier;
        return val;
      });

      list = list.filter((item) => (
        item.department.toLowerCase().includes(params.search)
        || item.department_desc.toLowerCase().includes(params.search)
        || item.department_cost.toLowerCase().includes(params.search)
      ));

      return [...list];
    },
    [dataMasterDepartment, params],
  );

  const {
    data: dataMasterDepartmentCost,
    isFetching: isFetchingDepartmentCost,
  } = useQuery({
    queryKey: "get_master_department_cost",
    queryFn: () => getMasterDepartmentCost(),
  });

  const handlePost = async () => {
    setLoading(true);
    if (editValues) {
      await putMasterDepartment({
        id: editValues.id,
        code_type: getValues("code_type"),
        department: getValues("department"),
        department_desc: getValues("department_desc"),
        department_cost: getValues("department_cost"),
        is_active: true,
        update_by: userInfo.preferred_username,
      }).then((res) => {
        if (res.responseCode === "Succeed") {
          refetchTable();
          reset(initDepartmentValues);
          setModalFormDepartment(false);
          setEditValues(undefined);
          setLoading(false);
          snackbar.show("Berhasil mengubah data", "success");
        } else {
          setLoading(false);
          snackbar.show(res.responseMessage || "Gagal mengubah data", "error");
        }
      }).catch(() => {
        setLoading(false);
        snackbar.show("Gagal mengubah data", "error");
      });
    } else {
      await postMasterDepartment({
        code_type: getValues("code_type"),
        department: getValues("department"),
        department_desc: getValues("department_desc"),
        department_cost: getValues("department_cost"),
        is_active: true,
        create_by: userInfo.preferred_username,
      }).then((res) => {
        if (res.responseCode === "Succeed") {
          refetchTable();
          reset(initDepartmentValues);
          setModalFormDepartment(false);
          setEditValues(undefined);
          setLoading(false);
          snackbar.show("Berhasil menambah data", "success");
        } else {
          setLoading(false);
          snackbar.show(res.responseMessage || "Gagal menambah data", "error");
        }
      }).catch(() => {
        setLoading(false);
        snackbar.show("Gagal menambah data", "error");
      });
    }
  };

  useEffect(() => {
    if (!modalFormDepartment && !modalDeleteDepartment) {
      setEditValues(undefined);
      reset(initDepartmentValues);
    }
  }, [modalFormDepartment, modalDeleteDepartment]);

  const handleDelete = async () => {
    setLoading(true);
    if (editValues) {
      await deleteMasterDepartment(editValues.id)
        .then((res) => {
          if (res.responseCode === "Succeed") {
            refetchTable();
            reset(initDepartmentValues);
            setModalDeleteDepartment(false);
            setEditValues(undefined);
            setLoading(false);
            snackbar.show("Berhasil manghapus data", "success");
          } else {
            setLoading(false);
            snackbar.show(res.responseMessage || "Gagal manghapus data", "error");
          }
        }).catch(() => {
          setLoading(false);
          snackbar.show("Gagal manghapus data", "error");
        });
    } else setLoading(false);
  };

  return {
    initDepartmentValues,
    isLoading,
    modalFormDepartment,
    setModalFormDepartment,
    modalDeleteDepartment,
    setModalDeleteDepartment,
    editValues,
    setEditValues,
    params,
    setParams,
    methods,
    masterDepartmentList,
    isFetching,
    masterDepartmentCostList: dataMasterDepartmentCost?.list_departmentcost,
    isFetchingDepartmentCost,
    handlePost,
    handleDelete,
  };
};

export default useDepartment;
